import { Button, Card, NoticeBox, StatCard, Table, Tags } from "../ui";
import { RequestProgress, rowsText } from "./ChecksStep";
import { APP_LOGOS, appLabel, sectionLabel } from "./reportAnalysisModel";
import { count, money } from "./format";
import { errorText } from "./reportAnalysisText";
import { CROSS_STORE } from "./reviewModel";

// Financial fields of the analysis summary (lud-backend utils/reporting/analysisSummary.js): label
// key, analyzer field, format. Only values the backend calculated are shown; a field the report type
// does not have reads "Not available". Nothing is added up or combined here.
export const METRICS = [
  ["orders", "completed", "count"],
  ["sales", "completedAmount", "money"],
  ["commission", "serviceCharge", "money"],
  ["paymentFees", "ccAmount", "money"],
  ["deliveryOffer", "deliveryOffer", "money"],
  ["offers", "offers", "money"],
  ["vat", "vat", "money"],
  ["compensation", "enduranceAmount", "money"],
  ["rejected", "rejected", "count"],
  ["canceled", "canceled", "count"],
  ["net", "grandTotal", "money"],
];
const TOTAL_CARDS = ["orders", "sales", "commission", "paymentFees", "compensation", "net"];

export const formatValue = (value, type, lang, text) => (typeof value === "number" ? (type === "money" ? money(value, lang) : count(value, lang)) : text.notAvailable);
export const staleCount = (rows) => (rows?.stale_owned || 0) + (rows?.stale_unowned || 0) + (rows?.stale_protected || 0);

function Metrics({ data, text, lang }) {
  if (!data) return null;
  const shown = METRICS.filter(([, field]) => field in (data.metrics || {}) || ["completed", "completedAmount", "grandTotal"].includes(field));
  return (
    <dl className="report-analysis-metrics">
      {shown.map(([key, field, type]) => (
        <div key={key} className={`report-analysis-metric ${typeof data.metrics?.[field] === "number" ? "" : "is-unavailable"}`}>
          <dt className="report-analysis-metric__label">{text.metrics[key]}</dt>
          <dd className="report-analysis-metric__value">{formatValue(data.metrics?.[field], type, lang, text)}</dd>
        </div>
      ))}
    </dl>
  );
}

// Where an application's records go when branches of another Store were acknowledged: the selected
// Store and its branches that receive records, and the excluded rows (never saved, never reassigned).
function Attribution({ item, text, lang, storeName }) {
  const p = text.preview2;
  const mismatches = item.periodWarnings.filter((warning) => warning.code === CROSS_STORE);
  if (!mismatches.length) return null;
  const branches = [...new Set((item.main?.branches || []).map((row) => row.branchName).filter(Boolean))];
  const excludedRows = mismatches.reduce((sum, warning) => sum + (warning.affectedRows || 0), 0);
  const excludedBranches = mismatches.reduce((sum, warning) => sum + (warning.items?.length || 0), 0);
  return (
    <NoticeBox variant="warning" size="sm">
      <div data-testid={`preview-attribution-${item.app}`}>
        <p><strong>{p.savedTo}:</strong> {storeName || "—"} — {branches.length ? branches.join("، ") : p.noBranches}</p>
        <p>{p.excludedRows(count(excludedRows, lang), count(excludedBranches, lang))}</p>
      </div>
    </NoticeBox>
  );
}

// One application's financial analysis and what saving it changes.
function AppPreview({ item, text, lang, mode, storeName, period }) {
  const f = text.flow;
  const p = text.preview2;
  const branchRows = item.main?.branches || [];
  const changes = item.main?.changes;
  return (
    <div data-testid={`preview-app-${item.app}`}>
    <Card className="report-analysis-card report-preview-app" hoverable={false}>
      <div className="report-analysis-appfiles__head">
        {APP_LOGOS[item.app] && <img src={APP_LOGOS[item.app]} alt="" aria-hidden="true" />}
        <h4>{appLabel(item.app, lang)} — {text.modes[mode]}</h4>
        {item.periodWarnings.length + item.warnings.length > 0 && <Tags variant="warning" size="sm">{text.review.warningsCount(item.periodWarnings.length + item.warnings.length, count(item.periodWarnings.length + item.warnings.length, lang))}</Tags>}
      </div>
      <p className="report-analysis-muted">
        {[item.sections.map((section) => sectionLabel(item.app, section, lang)).join("، "), typeof item.sourceRows === "number" ? `${p.sourceRows}: ${count(item.sourceRows, lang)}` : null, typeof item.branches === "number" ? `${p.branches}: ${count(item.branches, lang)}` : null].filter(Boolean).join(" · ")}
      </p>
      {item.accounting && <p className="report-analysis-muted" data-testid={`preview-rows-${item.app}`}>{rowsText(item.accounting, text, lang, period)}</p>}
      <Attribution item={item} text={text} lang={lang} storeName={storeName} />
      <Metrics data={item.main} text={text} lang={lang} />
      {item.compensation && (
        <dl className="report-analysis-metrics">
          <div className="report-analysis-metric"><dt className="report-analysis-metric__label">{p.compensationRows}</dt><dd className="report-analysis-metric__value">{count(item.compensation.rows, lang)}</dd></div>
          <div className="report-analysis-metric"><dt className="report-analysis-metric__label">{p.compensationTotal}</dt><dd className="report-analysis-metric__value">{formatValue(item.compensation.metrics?.enduranceAmount, "money", lang, text)}</dd></div>
        </dl>
      )}
      {item.database && (
        <p className="report-preview-db" data-testid={`preview-db-${item.app}`}>
          <strong>{f.databaseChanges}:</strong> {f.dbLine(count(item.database.insert, lang), count(item.database.update, lang), count(item.database.unchanged, lang))}
          {item.database.stale ? ` · ${count(item.database.stale, lang)} ${text.review.database.stale}` : ""}
        </p>
      )}
      {branchRows.length > 1 && (
        <details className="report-preview-more">
          <summary>{p.byBranch} ({count(branchRows.length, lang)})</summary>
          <Table
            columns={[
              { key: "branchName", label: p.branch, render: (row) => row.branchName || "—" },
              { key: "orders", label: text.metrics.orders, render: (row) => formatValue(row.metrics?.completed, "count", lang, text) },
              { key: "sales", label: text.metrics.sales, render: (row) => formatValue(row.metrics?.completedAmount, "money", lang, text) },
              { key: "compensation", label: text.metrics.compensation, render: (row) => formatValue(row.metrics?.enduranceAmount, "money", lang, text) },
              { key: "net", label: text.metrics.net, render: (row) => formatValue(row.metrics?.grandTotal, "money", lang, text) },
            ]}
            data={branchRows.map((row, index) => ({ ...row, id: row.branchRef || index }))}
          />
        </details>
      )}
      {changes && (
        <details className="report-preview-more" data-testid={`preview-changes-${item.app}`}>
          <summary>{p.changesTitle(changes.rows, count(changes.rows, lang))}</summary>
          <Table
            columns={[{ key: "label", label: p.field }, { key: "before", label: p.saved }, { key: "after", label: p.analyzed }, { key: "difference", label: p.difference }]}
            data={METRICS.filter(([, field]) => field in (changes.difference || {})).map(([key, field, type]) => ({
              id: key,
              label: text.metrics[key],
              before: formatValue(changes.before[field], type, lang, text),
              after: formatValue(changes.after[field], type, lang, text),
              difference: `${changes.difference[field] > 0 ? "+" : ""}${formatValue(changes.difference[field], type, lang, text)}`,
            }))}
          />
        </details>
      )}
    </Card>
    </div>
  );
}

// Step 4 - Preview: what saving would write, calculated by the server from the files. Saving needs
// the preview's plan fingerprint (review.canSave); nothing is saved without "Confirm & Save".
export default function PreviewStep({ text, lang, state, review, busy, onBack, onRetry, onSave }) {
  const f = text.flow;
  const { preview, error } = state;
  const apps = review.apps.filter((item) => item.files.length || item.main || item.errors.length);
  const requestFailed = error && error.step === "preview" && !error.notice && !busy;
  const totalOf = (key) => {
    const [, field, type] = METRICS.find(([name]) => name === key);
    const value = review.totals.metrics?.[field];
    return <StatCard key={key} label={text.metrics[key]} value={formatValue(value, type, lang, text)} className={typeof value === "number" ? "" : "is-unavailable"} />;
  };

  return (
    <section className="report-analysis-step" data-testid="step-preview" aria-label={f.previewTitle}>
      <header className="report-analysis-step__head">
        <h3>{f.previewTitle}</h3>
        <p className="report-analysis-muted">{f.previewHint}</p>
      </header>

      {busy && <RequestProgress text={text} phase={state.phase} progress={state.progress} label={f.previewing} />}
      {requestFailed && (
        <NoticeBox variant="danger" title={f.requestFailed} bordered>
          <div role="alert" data-testid="preview-failure">
            <p>{errorText(text, error)}</p>
            <p className="report-analysis-muted">{f.checksNeverSave}</p>
          </div>
        </NoticeBox>
      )}
      {error?.notice && error.step === "preview" && <NoticeBox variant="warning" size="sm"><span role="alert">{errorText(text, error)}</span></NoticeBox>}

      {preview && !busy && (
        <>
          <section aria-label={f.overview} className="report-preview-block">
            <h4>{f.overview}</h4>
            <div className="report-analysis-figures" data-testid="preview-totals">
              <StatCard label={f.appsAnalyzed} value={count(review.totals.apps, lang)} />
              <StatCard label={f.filesAnalyzed} value={count(review.totals.files, lang)} />
              <StatCard label={text.preview2.sourceRows} value={formatValue(review.totals.sourceRows, "count", lang, text)} className={typeof review.totals.sourceRows === "number" ? "" : "is-unavailable"} />
              {TOTAL_CARDS.map(totalOf)}
              <StatCard label={text.preview2.branches} value={formatValue(review.totals.branches, "count", lang, text)} className={typeof review.totals.branches === "number" ? "" : "is-unavailable"} />
            </div>
          </section>

          <section aria-label={f.appSummaries} className="report-preview-block">
            <h4>{f.appSummaries}</h4>
            <div className="report-analysis-grid">
              {apps.map((item) => <AppPreview key={item.app} item={item} text={text} lang={lang} mode={review.context.mode} storeName={review.context.storeName} period={review.context.period} />)}
            </div>
          </section>

        </>
      )}

      <div className="report-analysis-actions">
        <Button appearance="soft" onClick={onBack}>{f.backToChecks}</Button>
        {requestFailed && <Button variant="primary" appearance="soft" onClick={onRetry}>{f.retryPreview}</Button>}
        {preview && !busy && <Button variant="primary" appearance="filled" disabled={!review.canSave} onClick={onSave}>{f.confirmSave}</Button>}
      </div>
    </section>
  );
}
